/**
 * Dialog-card design tokens. The chrome (title row, close button, header
 * padding, floating footer) is NOT here — it lives in the one primitive that
 * paints it, `components/ui/dialog-card.tsx`. These are the surfaces it paints
 * ON, plus the z-scale.
 *
 * The four Z numbers below are the declared scale (`SHEET_CANON.md` §7: sheets
 * sit at 10120/10130 *relative to these*), so they stay exported even where
 * currently unreferenced — they are the numeric statement of the contract that
 * the `*_CLASS` strings below spell inline as `z-[…]`.
 *
 * Removed 2026-08-01 with the chrome consolidation, both at zero consumers:
 * `DIALOG_CARD_HEADER_CLASS`'s border-only sibling `DIALOG_CARD_HEADER_BORDER`,
 * and `DIALOG_CARD_FOOTER_CLASS` — a docked `border-t` bar, i.e. the shape
 * `OVERLAY_FOOTER.md` forbids, which is presumably why nothing ever used it.
 *
 * @see .tondocs/ui/DIALOGS.md — the canon; `DialogCard` is the only assembler
 */
export const DIALOG_CARD_OVERLAY_Z = 10100
export const DIALOG_CARD_CONTENT_Z = 10110
export const DIALOG_CARD_OVERLAY_Z_NESTED = 10120
export const DIALOG_CARD_CONTENT_Z_NESTED = 10130

export const DIALOG_CARD_OVERLAY_CLASS =
  "fixed inset-0 z-[10100] bg-black/70 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"

export const DIALOG_CARD_OVERLAY_CLASS_NESTED =
  "fixed inset-0 z-[10120] bg-black/70 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"

/**
 * The panel surface, shared with the mobile twin: the same `--sheet*` tokens
 * `SHEET_PANEL_DARK` paints with. One token set for both, deliberately — the
 * five "chrome-less" overlays render ONE `content` variable into both the
 * `DockSheet` and the `Dialog` branch, so a divergence here would be visible
 * as the same JSX looking different on either side of a resize.
 *
 * THEME-FOLLOWING since Phase 2 step 2f (the light-theme cutover): the forced
 * `dark` class was removed from both constants here together with
 * `SHEET_PANEL_DARK` and the two page-level flip points (see its header for
 * the full list of five). `_NESTED` is the easy one to miss on a rollback: it
 * is wired into the `AlertDialogContent` primitive, so no consumer file
 * mentions it.
 *
 * @see .tondocs/ui/HANDOFF_SHEET_LIGHT_THEME_2026_08_01.md §0.1, §4.1, §7
 */
export const DIALOG_CARD_CONTENT_BASE_NESTED =
  "z-[10130] w-full p-0 gap-0 border-0 bg-sheet rounded-[34px] sm:rounded-[34px] overflow-hidden text-sheet-foreground duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 isolate"

export const DIALOG_CARD_CONTENT_BASE =
  "z-[10110] w-full p-0 gap-0 border-0 bg-sheet rounded-[34px] sm:rounded-[34px] overflow-hidden text-sheet-foreground duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 isolate"

/**
 * NOT for product dialogs — `DialogCard` owns their header. Sole remaining
 * consumer is the dropdown dev showcase (`app/admin/dev/ui/dropdown-unification`),
 * which mocks up a card shell around dropdown specimens.
 */
export const DIALOG_CARD_HEADER_CLASS =
  "flex items-center justify-between p-4 border-b border-foreground/10"

/** Card-shaped body padding for surfaces that are not a `DialogCard`: `AlertDialog`, the dev showcase. */
export const DIALOG_CARD_BODY_CLASS =
  "p-4 max-h-[60vh] overflow-y-auto"
